import { z } from 'zod';

// src/common/pagination.ts
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20)
});
var sortDirectionSchema = z.enum(["asc", "desc"]);
function sortQuerySchema(fields) {
  return z.object({
    sortField: z.enum(fields).optional(),
    sortDir: sortDirectionSchema.optional()
  });
}
var paginationMetaSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0)
});
function paginatedResponseSchema(item) {
  return z.object({
    data: z.array(item),
    meta: paginationMetaSchema
  });
}
var bulkImportRequestSchema = z.object({
  /// Raw CSV text. Capped at ~12 MB (comfortably fits 10k+ rows). The import
  /// validates everything up front, then inserts in bulk (chunked createMany),
  /// so even a full 10k file finishes in seconds within one request. Dry-run
  /// (preview) vs commit is chosen by endpoint (`/bulk-import/preview` vs
  /// `/bulk-import`), not a body flag, so audit logging stays honest.
  csv: z.string().min(1).max(12582912)
});
var BULK_IMPORT_ERROR_CODES = [
  "VALIDATION_FAILED",
  "CONFLICT",
  "NOT_FOUND",
  "UNKNOWN_ERROR"
];
var bulkImportRowErrorSchema = z.object({
  /// 1-based source row (header is row 1, first data row is row 2) so the
  /// admin can jump straight to the bad cell in their spreadsheet.
  rowNumber: z.number().int().positive(),
  /// Human-identifying value for the row (unit number / owner name).
  label: z.string().nullable(),
  code: z.enum(BULK_IMPORT_ERROR_CODES),
  message: z.string()
});
var bulkImportPreviewRowSchema = z.object({
  rowNumber: z.number().int().positive(),
  values: z.record(z.string(), z.string().nullable())
});
var bulkImportResponseSchema = z.object({
  /// Echoes the request: true = nothing was written (preview only).
  dryRun: z.boolean(),
  totalRows: z.number().int().nonnegative(),
  /// Rows that passed validation AND don't conflict — i.e. what would be (or
  /// was) created. In a dry run this is the "will create N" number.
  validCount: z.number().int().nonnegative(),
  /// Rows actually written. Always 0 on a dry run.
  createdCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  createdIds: z.array(z.string().uuid()),
  errors: z.array(bulkImportRowErrorSchema),
  /// A capped sample of the valid rows, for the "here's what you're importing"
  /// preview table. Empty when there is nothing to import.
  preview: z.array(bulkImportPreviewRowSchema)
});
var UNIT_CSV_HEADERS = ["unitNumber", "building", "floor", "notes"];
var OWNER_CSV_HEADERS = [
  "fullName",
  "phoneNumber",
  "email",
  "nationalId",
  "notes"
];
var isoTimestampSchema = z.string().datetime({ offset: true });
var timestampsSchema = z.object({
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema
});

// src/common/errors.ts
var errorResponseSchema = z.object({
  statusCode: z.number().int(),
  error: z.string(),
  message: z.union([z.string(), z.array(z.string())]),
  requestId: z.string().optional(),
  timestamp: isoTimestampSchema,
  path: z.string().optional()
});
var validationErrorResponseSchema = errorResponseSchema.extend({
  statusCode: z.literal(400),
  error: z.literal("Bad Request"),
  details: z.array(
    z.object({
      path: z.array(z.union([z.string(), z.number()])),
      message: z.string(),
      code: z.string().optional()
    })
  ).optional()
});
var uuidSchema = z.string().uuid();
var adminRoleSchema = z.enum(["VIEWER", "MODERATOR", "REVIEWER", "SUPER_ADMIN"]);
var bootstrapAdminRequestSchema = z.object({
  email: z.string().email().max(254).toLowerCase(),
  password: z.string().min(10).max(256),
  fullName: z.string().min(2).max(120).trim()
});
var adminResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  fullName: z.string(),
  role: adminRoleSchema,
  isActive: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
  lastLoginAt: z.string().datetime({ offset: true }).nullable()
});
var ADMIN_SORT_FIELDS = ["fullName", "email", "role", "createdAt", "lastLoginAt"];
var listAdminsQuerySchema = paginationQuerySchema.merge(sortQuerySchema(ADMIN_SORT_FIELDS)).extend({
  search: z.string().trim().min(1).max(120).optional(),
  role: adminRoleSchema.optional(),
  isActive: z.coerce.boolean().optional()
});
var createAdminRequestSchema = z.object({
  email: z.string().email().max(254).toLowerCase(),
  password: z.string().min(10).max(256),
  fullName: z.string().trim().min(2).max(120),
  role: adminRoleSchema
});
var updateAdminRequestSchema = z.object({
  fullName: z.string().trim().min(2).max(120).optional(),
  role: adminRoleSchema.optional(),
  isActive: z.boolean().optional()
}).refine((v) => Object.keys(v).length > 0, { message: "No changes provided" });
var resetAdminPasswordRequestSchema = z.object({
  password: z.string().min(10).max(256).optional()
});
var resetAdminPasswordResponseSchema = z.object({
  /// The new plaintext password to hand to the admin (shown once).
  password: z.string()
});
var REMINDER_SORT_FIELDS = ["remindAt", "createdAt", "sentAt"];
var reminderTypeSchema = z.enum(["CUSTOM", "EXPIRY_APPROACHING", "PENDING_REVIEW"]);
var createReminderRequestSchema = z.object({
  recipientEmail: z.string().trim().email().max(254).toLowerCase(),
  remindAt: z.string().datetime({ offset: true }),
  note: z.string().trim().max(1e3).optional(),
  targetType: z.string().trim().max(40).optional(),
  targetId: z.string().trim().max(64).optional()
});
var reminderResponseSchema = z.object({
  id: z.string().uuid(),
  type: reminderTypeSchema,
  recipientEmail: z.string(),
  remindAt: z.string().datetime({ offset: true }),
  sentAt: z.string().datetime({ offset: true }).nullable(),
  sendError: z.string().nullable(),
  note: z.string().nullable(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  createdById: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true })
});
var listRemindersQuerySchema = paginationQuerySchema.merge(sortQuerySchema(REMINDER_SORT_FIELDS)).extend({
  type: reminderTypeSchema.optional(),
  /// 'pending' = not yet sent; 'sent' = already sent.
  state: z.enum(["pending", "sent"]).optional(),
  targetType: z.string().trim().max(40).optional(),
  targetId: z.string().trim().max(64).optional()
});
var adminStatsResponseSchema = z.object({
  /** Submissions awaiting review (status = PENDING). */
  pendingSubmissions: z.number().int().nonnegative(),
  /** Total delegated (login-capable) users. */
  delegatedUsers: z.number().int().nonnegative(),
  /** Total workers (no login). */
  workers: z.number().int().nonnegative(),
  /** Admin-activity audit events in the last 24 hours. */
  auditEvents24h: z.number().int().nonnegative()
});
var OWNER_SORT_FIELDS = ["fullName", "email", "createdAt"];
var EGYPT_MOBILE_NSN = /^1[0125]\d{8}$/;
var EGYPT_PHONE_MESSAGE = "Egyptian mobile must be 10 digits after the country code (e.g. 1012345678, starting 10/11/12/15)";
var phoneSchema = z.string().trim().superRefine((v, ctx) => {
  const fail = (message) => {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  };
  let egyptNsn = null;
  if (v.startsWith("+20")) egyptNsn = v.slice(3);
  else if (v.startsWith("0020")) egyptNsn = v.slice(4);
  else if (/^0\d+$/.test(v)) egyptNsn = v.slice(1);
  if (egyptNsn !== null) {
    if (!EGYPT_MOBILE_NSN.test(egyptNsn)) fail(EGYPT_PHONE_MESSAGE);
    return;
  }
  if (!/^\+?[1-9]\d{6,14}$/.test(v)) fail("Enter a valid phone number");
});
var egyptianMobileSchema = phoneSchema;
var optionalPhoneSchema = z.preprocess(
  (v) => typeof v === "string" && v.trim() === "" ? null : v,
  phoneSchema.nullish()
);
var idTypeSchema = z.enum(["NATIONAL_ID", "PASSPORT"]);
var idNumberSchema = z.string().trim().regex(/^[A-Za-z0-9]{5,20}$/, "ID number must be 5\u201320 letters or digits");
var egyptianNationalIdSchema = idNumberSchema;
function refineIdentity(data, ctx) {
  const id = data.nationalId?.trim();
  if (!id) return;
  const type = data.idType ?? "NATIONAL_ID";
  const ok = type === "NATIONAL_ID" ? /^\d{14}$/.test(id) : /^[A-Za-z0-9]{5,20}$/.test(id);
  if (!ok) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["nationalId"],
      message: type === "NATIONAL_ID" ? "National ID must be exactly 14 digits" : "Passport must be 5\u201320 letters or digits"
    });
  }
}
var createOwnerFields = z.object({
  fullName: z.string().trim().min(2).max(160),
  email: z.string().trim().email().max(254).toLowerCase().nullish(),
  phoneNumber: phoneSchema,
  idType: idTypeSchema.optional(),
  nationalId: idNumberSchema.nullish(),
  notes: z.string().trim().max(500).nullish(),
  /// Units this owner holds. Assigning a unit here sets that unit's owner
  /// (an owner may hold many units); units removed on update are detached.
  unitIds: z.array(z.string().uuid()).max(500).optional()
});
var createOwnerRequestSchema = createOwnerFields.superRefine(refineIdentity);
var updateOwnerRequestSchema = createOwnerFields.omit({ nationalId: true, idType: true }).partial().extend({
  isBlocked: z.boolean().optional(),
  blockedReason: z.string().trim().max(500).nullish()
});
var ownerResponseSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  email: z.string().nullable(),
  phoneNumber: z.string(),
  idType: idTypeSchema,
  nationalId: z.string().nullable(),
  notes: z.string().nullable(),
  isBlocked: z.boolean(),
  blockedAt: z.string().datetime({ offset: true }).nullable(),
  blockedReason: z.string().nullable(),
  unitCount: z.number().int().min(0),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true })
});
var listOwnersQuerySchema = paginationQuerySchema.merge(sortQuerySchema(OWNER_SORT_FIELDS)).extend({
  search: z.string().trim().min(1).max(80).optional(),
  isBlocked: z.union([z.boolean(), z.enum(["true", "false"])]).transform((v) => typeof v === "boolean" ? v : v === "true").optional()
});

// src/delegated-users/delegated-users.ts
var DELEGATED_USER_SORT_FIELDS = ["username", "fullName", "status", "createdAt"];
var personStatusSchema = z.enum(["PENDING", "APPROVED", "BLOCKED", "REVOKED", "EXPIRED"]);
var hikSyncStatusSchema = z.enum(["PENDING", "SYNCED", "FAILED"]);
var usernameSchema = z.string().trim().min(3).max(40).regex(/^[a-z0-9._-]+$/i, "Username may contain letters, digits, dot, underscore, dash").toLowerCase();
var delegatedUserUnitSchema = z.object({
  id: z.string().uuid(),
  unitId: z.string().uuid(),
  unitNumber: z.string(),
  status: personStatusSchema,
  validFrom: z.string().datetime({ offset: true }).nullable(),
  validTo: z.string().datetime({ offset: true }).nullable(),
  blockedReason: z.string().nullable(),
  blockedByUnit: z.boolean(),
  /// True when a finishing-works permit was captured for this delegate on this
  /// unit (at approval) and can be downloaded/printed. False for admin-created
  /// delegates and links approved before permits were snapshotted.
  hasPermit: z.boolean()
});
var createDelegatedUserRequestSchema = z.object({
  /// Credentials are auto-generated server-side (identical to the public
  /// owner-submission flow) and sent to the delegate over WhatsApp + email —
  /// admins never type them. Kept optional for legacy/scripted callers that
  /// still pass explicit values.
  username: usernameSchema.optional(),
  password: z.string().min(12).max(256).optional(),
  email: z.string().trim().email().max(254).toLowerCase().nullish(),
  fullName: z.string().trim().min(2).max(160),
  phoneNumber: phoneSchema,
  idType: idTypeSchema.optional(),
  nationalId: idNumberSchema.nullish(),
  /// The units this delegate may access (at least one). Each becomes a
  /// DelegatedUserUnit link.
  unitIds: z.array(z.string().uuid()).min(1, "Select at least one unit"),
  validFrom: z.string().datetime({ offset: true }).nullish(),
  validTo: z.string().datetime({ offset: true }).nullish(),
  notes: z.string().trim().max(500).nullish(),
  /// Storage keys returned by POST /files. The face photo is required by
  /// HikCentral face enrollment; the raw ID-card scan is kept for audit.
  /// Optional here so an admin can create the record first and attach the
  /// photo later — but without a face photo the HikCentral sync will fail
  /// (surfaced via hikSyncStatus=FAILED), so the form strongly nudges it.
  facePhotoKey: z.string().trim().min(1).max(512).nullish(),
  idCardKey: z.string().trim().min(1).max(512).nullish()
}).superRefine(refineIdentity);
var updateDelegatedUserRequestSchema = z.object({
  email: z.string().trim().email().max(254).toLowerCase().nullish(),
  fullName: z.string().trim().min(2).max(160).optional(),
  phoneNumber: phoneSchema.optional(),
  // Anti-fraud: nationalId + idType are IMMUTABLE after creation and are
  // deliberately not accepted here — no one can swap a delegate's identity.
  /// Account-wide status. Setting BLOCKED here is the delegate-level ban
  /// (denies login everywhere). Unit membership is managed via the dedicated
  /// /units endpoints, not this generic update.
  status: personStatusSchema.optional(),
  validFrom: z.string().datetime({ offset: true }).nullish(),
  validTo: z.string().datetime({ offset: true }).nullish(),
  notes: z.string().trim().max(500).nullish(),
  /// Admin-flagged violation note printed on the delegate's permit. Set/edit it
  /// here to flag ANY delegate (old or new); null/'' clears it.
  violationNote: z.string().trim().max(2e3).nullish(),
  /// Toggle admin expiry-approaching reminders for this delegate.
  expiryReminderEnabled: z.boolean().optional()
});
var addDelegatedUserUnitRequestSchema = z.object({
  unitId: z.string().uuid(),
  validFrom: z.string().datetime({ offset: true }).nullish(),
  validTo: z.string().datetime({ offset: true }).nullish()
});
var blockDelegatedUserUnitRequestSchema = z.object({
  reason: z.string().trim().max(500).optional()
});
var bulkUpdateUnitWindowsRequestSchema = z.object({
  delegateIds: z.array(z.string().uuid()).optional(),
  validFrom: z.string().datetime({ offset: true }).nullish(),
  validTo: z.string().datetime({ offset: true }).nullish()
}).superRefine((val, ctx) => {
  if (val.validFrom === void 0 && val.validTo === void 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Provide a start and/or end date to apply.",
      path: ["validTo"]
    });
  }
});
var bulkUpdateUnitWindowsResponseSchema = z.object({
  /// How many delegates on the unit had their window updated.
  updated: z.number().int().nonnegative()
});
var cancelDelegationRequestSchema = z.object({
  reason: z.string().trim().max(500).optional()
});
var bulkCancelDelegationsRequestSchema = z.object({
  delegateIds: z.array(z.string().uuid()).optional(),
  reason: z.string().trim().max(500).optional()
});
var bulkCancelDelegationsResponseSchema = z.object({
  /// How many delegations on the unit were cancelled.
  cancelled: z.number().int().nonnegative()
});
var gateSyncRetryResultSchema = z.object({
  scanned: z.number().int().nonnegative(),
  requeued: z.number().int().nonnegative(),
  flagged: z.number().int().nonnegative()
});
var bulkRetryFailedSyncResponseSchema = z.object({
  delegates: gateSyncRetryResultSchema,
  workers: gateSyncRetryResultSchema
});
var gateSyncRetryStartedResponseSchema = z.object({
  started: z.boolean(),
  queued: z.number().int().nonnegative()
});
var gateSyncStatusResponseSchema = z.object({
  running: z.boolean(),
  total: z.number().int().nonnegative(),
  processed: z.number().int().nonnegative(),
  requeued: z.number().int().nonnegative(),
  flagged: z.number().int().nonnegative(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  failedRemaining: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative()
});
var delegatedUserResponseSchema = z.object({
  id: z.string().uuid(),
  username: z.string(),
  email: z.string().nullable(),
  fullName: z.string(),
  phoneNumber: z.string(),
  idType: idTypeSchema,
  nationalId: z.string().nullable(),
  /// All units this delegate can access (multi-unit). Each carries its own
  /// status (unit-level ban) + validity window.
  units: z.array(delegatedUserUnitSchema),
  status: personStatusSchema,
  validFrom: z.string().datetime({ offset: true }).nullable(),
  validTo: z.string().datetime({ offset: true }).nullable(),
  hikSyncStatus: hikSyncStatusSchema,
  hikSyncedAt: z.string().datetime({ offset: true }).nullable(),
  hikSyncError: z.string().nullable(),
  blockedByExternalApi: z.boolean(),
  matchedBlockedPersonId: z.string().nullable(),
  externalBlockedReason: z.string().nullable(),
  facePhotoKey: z.string().nullable(),
  idCardKey: z.string().nullable(),
  notes: z.string().nullable(),
  /// Admin-flagged violation note, printed on this delegate's permit.
  violationNote: z.string().nullable(),
  /// Who last edited this delegate via the admin form + when (dashboard only).
  lastModifiedByName: z.string().nullable(),
  lastModifiedAt: z.string().datetime({ offset: true }).nullable(),
  /// Append-only reactivation history — each time an EXPIRED delegate was
  /// re-approved via a new owner submission (preserves the prior window).
  reactivations: z.array(
    z.object({
      at: z.string(),
      byName: z.string(),
      previousValidFrom: z.string().nullable(),
      previousValidTo: z.string().nullable(),
      newValidFrom: z.string().nullable(),
      newValidTo: z.string().nullable()
    })
  ).nullable(),
  /// Whether admins get an expiry-approaching reminder for this delegate.
  expiryReminderEnabled: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true })
});
var listDelegatedUsersQuerySchema = paginationQuerySchema.merge(sortQuerySchema(DELEGATED_USER_SORT_FIELDS)).extend({
  search: z.string().trim().min(1).max(80).optional(),
  status: personStatusSchema.optional(),
  unitId: z.string().uuid().optional(),
  /// Inclusive createdAt range (ISO datetime).
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional()
});
var resendChannelSchema = z.enum(["both", "whatsapp", "email"]);
var resendCredentialsRequestSchema = z.object({
  channel: resendChannelSchema,
  unitId: z.string().uuid().optional(),
  email: z.string().trim().email().max(254).toLowerCase().nullish(),
  phoneNumber: phoneSchema.optional()
});
var resendCredentialsResponseSchema = z.object({
  channel: resendChannelSchema,
  /// True when a credentials email was enqueued (delegate had an email + email
  /// was a requested channel).
  emailSent: z.boolean(),
  /// True when the WhatsApp send was accepted by the provider.
  whatsappSent: z.boolean(),
  message: z.string()
});

// src/auth/login.ts
var adminLoginRequestSchema = z.object({
  email: z.string().email().max(254).toLowerCase(),
  password: z.string().min(1).max(256)
});
var adminLoginResponseSchema = z.object({
  accessToken: z.string().min(1),
  accessTokenExpiresAt: z.string().datetime({ offset: true }),
  admin: adminResponseSchema
});
var refreshResponseSchema = z.object({
  accessToken: z.string().min(1),
  accessTokenExpiresAt: z.string().datetime({ offset: true })
});
var userLoginRequestSchema = z.object({
  /// Username OR email — the server resolves either to a delegate account.
  /// Kept permissive (emails contain `@`, which the strict username schema
  /// rejects); lowercased so both lookups are case-insensitive.
  username: z.string().trim().min(3).max(254).toLowerCase(),
  password: z.string().min(1).max(256)
});
var delegatePrincipalSchema = z.object({
  id: z.string().uuid(),
  username: z.string(),
  fullName: z.string(),
  email: z.string().nullable(),
  /// The units this delegate can currently manage. Multi-unit: one entry per
  /// active (non-blocked) unit link. The portal uses this to scope worker
  /// management and offer a unit switcher.
  units: z.array(
    z.object({
      id: z.string().uuid(),
      unitNumber: z.string(),
      status: personStatusSchema,
      /// True when a downloadable finishing-works permit exists for this
      /// delegate on this unit (lets the portal show a "my permit" action).
      hasPermit: z.boolean(),
      /// The delegate's EFFECTIVE authorization window for this unit — the
      /// intersection of their account window and this unit link. Null on either
      /// side means open-ended. The portal uses it to bound + default the dates
      /// on the worker form so a delegate can't request access outside their own.
      validFrom: z.string().datetime({ offset: true }).nullable(),
      validTo: z.string().datetime({ offset: true }).nullable()
    })
  ),
  status: personStatusSchema,
  mustChangePassword: z.boolean()
});
var userLoginResponseSchema = z.object({
  accessToken: z.string().min(1),
  accessTokenExpiresAt: z.string().datetime({ offset: true }),
  delegatedUser: delegatePrincipalSchema,
  /// True on first login after approval — the portal forces a password change.
  mustChangePassword: z.boolean()
});
var changePasswordRequestSchema = z.object({
  /// Optional: on the FORCED first-login change the delegate has just
  /// authenticated with the temp password, so it isn't re-required. Voluntary
  /// changes still require + verify it (enforced server-side).
  currentPassword: z.string().max(256).optional(),
  newPassword: z.string().min(10).max(256)
});
var meResponseSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("admin"), admin: adminResponseSchema }),
  z.object({ type: z.literal("delegated_user"), delegatedUser: delegatePrincipalSchema })
]);
var passwordResetRequestSchema = z.object({
  email: z.string().email().max(254).toLowerCase()
});
var passwordResetConfirmSchema = z.object({
  token: z.string().min(20).max(512),
  newPassword: z.string().min(10).max(256)
});

// src/auth/permissions.ts
var PERMISSIONS = [
  // Submissions
  "view_submissions",
  "approve_submissions",
  "reject_submissions",
  "edit_submissions",
  "reopen_submissions",
  "delete_submissions",
  // Users (delegated users)
  "view_users",
  "create_users",
  "delete_users",
  "block_users",
  "resend_credentials",
  // Workers
  "view_workers",
  "create_workers",
  "edit_workers",
  "delete_workers",
  "block_workers",
  "export_workers",
  // Units
  "view_units",
  "create_units",
  "delete_units",
  "block_units",
  // Admin management
  "view_admins",
  "create_admins",
  "edit_admins",
  "delete_admins",
  "manage_roles",
  // System
  "view_audit_logs",
  "view_entry_logs",
  "view_events",
  "view_sessions",
  "revoke_sessions",
  "reset_passwords",
  "system_settings",
  "manage_reminders"
];
var VIEWER_PERMISSIONS = [
  "view_submissions",
  "view_users",
  "view_workers",
  "view_units",
  "view_audit_logs",
  "view_entry_logs",
  "view_events",
  "export_workers"
];
var ROLE_PERMISSIONS = {
  VIEWER: VIEWER_PERMISSIONS,
  REVIEWER: [
    ...VIEWER_PERMISSIONS,
    "approve_submissions",
    "reject_submissions",
    "edit_submissions",
    "reopen_submissions",
    "create_users",
    "resend_credentials",
    "manage_reminders"
  ],
  MODERATOR: [
    ...VIEWER_PERMISSIONS,
    // Submission review — a moderator can accept/reject/edit/reopen owner requests.
    "approve_submissions",
    "reject_submissions",
    "edit_submissions",
    "reopen_submissions",
    "edit_workers",
    "delete_workers",
    "block_workers",
    "block_users",
    "resend_credentials",
    "block_units",
    "delete_units",
    "view_sessions",
    "revoke_sessions",
    "manage_reminders"
  ],
  SUPER_ADMIN: PERMISSIONS
};
function hasPermission(role, permission) {
  return ROLE_PERMISSIONS[role].includes(permission);
}
function hasAnyPermission(role, permissions) {
  return permissions.some((p) => hasPermission(role, p));
}
function hasAllPermissions(role, permissions) {
  return permissions.every((p) => hasPermission(role, p));
}
var localizedTextSchema = z.object({
  ar: z.string().trim().min(1),
  en: z.string().trim().min(1)
});
var localizedListSchema = z.object({
  ar: z.array(z.string().trim().min(1)).min(1),
  en: z.array(z.string().trim().min(1)).min(1)
});
var brandBankAccountSchema = z.object({
  beneficiary: z.string().trim().min(1),
  bank: z.string().trim().min(1),
  /// Not every client gives one — the row is hidden when absent.
  branch: z.string().trim().min(1).optional(),
  account: z.string().trim().min(1),
  iban: z.string().trim().min(1)
});
var formServiceSchema = z.enum([
  /// الشيكات التأمينية — security-deposit cheques.
  "insuranceChecks",
  /// عداد مياه — own water meter.
  "waterMeter",
  /// عداد كهرباء vs. ممارسة الكهرباء (usage fee, paid by transfer).
  "electricity",
  /// توصيل العمالة — labour transport (fee, paid by transfer).
  "laborTransport",
  /// تعاقدات المرافق — a one-off utilities package; unsubscribed owners pay it by
  /// transfer and must attach the receipt.
  "utilitiesBundle"
]);
var brandFormSchema = z.object({
  /// Unit-service options offered, in display order. Empty = no services card.
  services: z.array(formServiceSchema),
  /// What the utilities package contains. Required when it is offered.
  utilitiesBundle: z.object({ items: localizedListSchema }).nullable(),
  /// "Materials entry permits" notice near the end of the form (the compound's
  /// own list). Null hides it.
  entryPermits: z.object({
    intro: localizedTextSchema,
    items: localizedListSchema,
    /// Issuing department, printed under the list. Optional.
    signature: localizedTextSchema.nullable()
  }).nullable()
});
var brandingResponseSchema = z.object({
  /// Brand folder id, e.g. `hyde-park`.
  id: z.string(),
  /// Full display name — page title, footers, logo alt text.
  name: localizedTextSchema,
  /// Compact wordmark name.
  shortName: localizedTextSchema,
  bankAccount: brandBankAccountSchema.nullable(),
  form: brandFormSchema,
  /// Delivery channels this deployment really sends on (a configured driver),
  /// so the UI never promises a WhatsApp message that won't arrive.
  channels: z.object({ whatsapp: z.boolean(), email: z.boolean() }),
  /// Content hash of the logo + icon. Appended to the asset URLs
  /// (`/branding/logo?v=…`) so a changed logo is never served from cache.
  assetsVersion: z.string()
});
var entryDirectionSchema = z.enum(["ENTRY", "EXIT"]);
var ENTRY_LOG_SORT_FIELDS = ["scannedAt", "createdAt"];
var workerEntryLogResponseSchema = z.object({
  id: z.string().uuid(),
  workerId: z.string().uuid(),
  workerName: z.string(),
  unitNumber: z.string().nullable(),
  nationalId: z.string().nullable(),
  direction: entryDirectionSchema,
  scannedAt: z.string().datetime({ offset: true }),
  doorName: z.string().nullable(),
  /// Scan is past the configured curfew — a late departure to flag.
  late: z.boolean(),
  /// This scan (or the overstay sweep) auto-banned the worker.
  autoBanned: z.boolean(),
  createdAt: z.string().datetime({ offset: true })
});
var listEntryLogsQuerySchema = paginationQuerySchema.merge(sortQuerySchema(ENTRY_LOG_SORT_FIELDS)).extend({
  /// Free-text over worker name / national id.
  search: z.string().trim().min(1).max(80).optional(),
  workerId: z.string().uuid().optional(),
  /// Only late (past-curfew) scans.
  lateOnly: z.coerce.boolean().optional(),
  /// ISO date range (inclusive) on scannedAt.
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional()
});
var entryLogStatsResponseSchema = z.object({
  /// Workers whose latest scan today is an ENTRY and it's now past the curfew —
  /// i.e. still inside when they should have left. THE big alert.
  overstayingNow: z.number().int().nonnegative(),
  /// Late departures (EXIT past curfew) recorded today.
  lateToday: z.number().int().nonnegative(),
  /// Workers auto-banned by the overstay rule today.
  autoBannedToday: z.number().int().nonnegative()
});
var recordAccessEventRequestSchema = z.object({
  /// HikCentral's opaque event id — used to de-dupe re-delivered events.
  eventId: z.string().trim().min(1).max(128).optional(),
  /// The scanned person's national id — correlated to a worker.
  nationalId: idNumberSchema,
  /// ENTRY / EXIT. If omitted, inferred from the worker's current in/out state.
  direction: entryDirectionSchema.optional(),
  scannedAt: z.string().datetime({ offset: true }),
  doorName: z.string().trim().max(120).optional()
});
var recordAccessEventResponseSchema = z.object({
  /// 'recorded' | 'duplicate' (eventId already seen) | 'unmatched' (no worker).
  outcome: z.enum(["recorded", "duplicate", "unmatched"]),
  entryLogId: z.string().uuid().nullable(),
  direction: entryDirectionSchema.nullable(),
  late: z.boolean(),
  autoBanned: z.boolean()
});
var optionalText = (max) => z.string().trim().max(max).optional().transform((v) => v && v.length > 0 ? v : void 0);
var requireOneBody = (data, ctx) => {
  if (!data.bodyAr && !data.bodyEn) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Provide the rule text in Arabic or English (at least one).",
      path: ["bodyAr"]
    });
  }
};
var formRuleResponseSchema = z.object({
  id: z.string().uuid(),
  titleAr: z.string().nullable(),
  titleEn: z.string().nullable(),
  bodyAr: z.string().nullable(),
  bodyEn: z.string().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true })
});
var createFormRuleRequestSchema = z.object({
  titleAr: optionalText(160),
  titleEn: optionalText(160),
  bodyAr: optionalText(4e3),
  bodyEn: optionalText(4e3),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  isActive: z.boolean().optional()
}).superRefine(requireOneBody);
var updateFormRuleRequestSchema = z.object({
  titleAr: optionalText(160),
  titleEn: optionalText(160),
  bodyAr: optionalText(4e3),
  bodyEn: optionalText(4e3),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  isActive: z.boolean().optional()
}).superRefine((data, ctx) => {
  if ("bodyAr" in data && "bodyEn" in data && !data.bodyAr && !data.bodyEn) {
    requireOneBody(data, ctx);
  }
});
var feePerMonthEgpSchema = z.number().int().min(0).max(1e6);
var priceEgpSchema = z.number().int().min(0).max(1e7);
var curfewTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Enter a time as HH:mm").nullable();
var graceMinutesSchema = z.number().int().min(0).max(720);
var leadDaysSchema = z.number().int().min(0).max(365);
var loginLeadDaysSchema = z.number().int().min(0).max(30);
var formSettingsResponseSchema = z.object({
  electricityFeePerMonthEgp: feePerMonthEgpSchema.nullable(),
  laborTransportFeePerMonthEgp: feePerMonthEgpSchema.nullable(),
  utilitiesBundlePriceEgp: priceEgpSchema.nullable(),
  /// Worker entry-log curfew config.
  workerCurfew: curfewTimeSchema,
  workerCurfewGraceMinutes: graceMinutesSchema,
  workerAutoBanEnabled: z.boolean(),
  startDateMaxLeadDays: leadDaysSchema,
  delegateLoginLeadDays: loginLeadDaysSchema,
  updatedAt: z.string().datetime({ offset: true }).nullable()
});
var publicFormSettingsResponseSchema = z.object({
  electricityFeePerMonthEgp: feePerMonthEgpSchema.nullable(),
  laborTransportFeePerMonthEgp: feePerMonthEgpSchema.nullable(),
  utilitiesBundlePriceEgp: priceEgpSchema.nullable(),
  startDateMaxLeadDays: leadDaysSchema
});
var updateFormSettingsRequestSchema = z.object({
  electricityFeePerMonthEgp: feePerMonthEgpSchema.nullable(),
  laborTransportFeePerMonthEgp: feePerMonthEgpSchema.nullable(),
  utilitiesBundlePriceEgp: priceEgpSchema.nullable(),
  workerCurfew: curfewTimeSchema,
  workerCurfewGraceMinutes: graceMinutesSchema,
  workerAutoBanEnabled: z.boolean(),
  startDateMaxLeadDays: leadDaysSchema,
  delegateLoginLeadDays: loginLeadDaysSchema
}).partial();
var SUBMISSION_SORT_FIELDS = ["createdAt", "unitNumber", "status"];
var submissionStatusSchema = z.enum(["PENDING", "APPROVED", "REJECTED"]);
var submissionTypeSchema = z.enum(["DELEGATED_USER", "WORKER"]);
var localeSchema = z.enum(["ar", "en"]);
var storageKeySchema = z.string().trim().min(1).max(512).regex(/^[\w./-]+$/, "Invalid storage key");
var submissionPersonSchema = z.object({
  fullName: z.string().trim().min(2).max(160),
  phoneNumber: phoneSchema,
  email: z.string().trim().email().max(254).toLowerCase().nullish(),
  idType: idTypeSchema.optional(),
  nationalId: idNumberSchema.nullish(),
  /** Required only for delegated users (they have a login). */
  username: z.string().trim().min(3).max(40).regex(/^[a-z0-9._-]+$/i, "Username may contain letters, digits, dot, underscore, dash").toLowerCase().optional(),
  /** Required only for delegated users (they have a login). */
  password: z.string().min(12).max(256).optional(),
  /** Required only for workers (validity window). */
  validFrom: z.string().datetime({ offset: true }).nullish(),
  validTo: z.string().datetime({ offset: true }).nullish(),
  /// Cropped face photo (client-side face detection picks the bounding
  /// box from the uploaded ID card scan, crops + uploads). Forwarded to
  /// HikCentral verbatim as the `facePhoto` payload.
  facePhotoKey: storageKeySchema.nullish(),
  /// Raw ID card scan (full image). Stored for audit; not forwarded to
  /// HikCentral by default.
  idCardKey: storageKeySchema.nullish()
});
var MAX_DELEGATES_PER_SUBMISSION = 4;
var MAX_SUBMISSION_ATTACHMENTS = 20;
var submissionAttachmentSchema = z.object({
  label: z.string().trim().max(120),
  fileKey: storageKeySchema
});
var personNameSchema = z.string().trim().min(1).max(80);
var submissionDurationSchema = z.enum([
  "ONE_WEEK",
  "ONE_MONTH",
  "TWO_MONTHS",
  "THREE_MONTHS",
  "FOUR_MONTHS"
]);
function addSubmissionDuration(from, duration) {
  const d = new Date(from);
  switch (duration) {
    case "ONE_WEEK":
      d.setDate(d.getDate() + 7);
      break;
    case "ONE_MONTH":
      d.setMonth(d.getMonth() + 1);
      break;
    case "TWO_MONTHS":
      d.setMonth(d.getMonth() + 2);
      break;
    case "THREE_MONTHS":
      d.setMonth(d.getMonth() + 3);
      break;
    case "FOUR_MONTHS":
      d.setMonth(d.getMonth() + 4);
      break;
  }
  return d;
}
function durationToMonths(duration) {
  switch (duration) {
    case "ONE_WEEK":
      return 0.25;
    case "ONE_MONTH":
      return 1;
    case "TWO_MONTHS":
      return 2;
    case "THREE_MONTHS":
      return 3;
    case "FOUR_MONTHS":
      return 4;
  }
}
function estimateElectricityFee(perMonthEgp, duration) {
  if (perMonthEgp == null) return null;
  return Math.round(perMonthEgp * durationToMonths(duration));
}
var isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick a valid date").refine((s) => !Number.isNaN(Date.parse(s)), "Pick a valid date");
var submissionServicesSchema = z.object({
  /// الشيكات التأمينية — security-deposit cheques handed over.
  insuranceChecks: z.boolean().optional(),
  /// عداد مياه — water meter included.
  waterMeter: z.boolean().optional(),
  /// خدمة توصيل العمالة للوحدة — labour transport to the unit.
  laborTransport: z.boolean().optional(),
  /// عداد كهرباء (own meter) vs. ممارسة الكهرباء (usage-based billing that may
  /// incur the estimated fee). Nullish = not specified.
  electricity: z.enum(["METER", "PRACTICE"]).nullish(),
  /// تعاقدات المرافق — the owner is already subscribed to the compound's utilities
  /// package. Only on deployments that offer it (brand `form.services`).
  utilitiesBundle: z.boolean().optional(),
  /// Transfer receipt for the package fee. Required when the package is
  /// offered and the owner is NOT subscribed (see needsUtilitiesBundleReceipt).
  /// '' is the form's "no file yet".
  utilitiesBundleReceiptKey: storageKeySchema.or(z.literal("")).nullish()
});
function needsUtilitiesBundleReceipt(services, offered) {
  return offered && services?.utilitiesBundle !== true && !services?.utilitiesBundleReceiptKey;
}
var submissionDelegateSchema = z.object({
  firstName: personNameSchema,
  lastName: personNameSchema,
  /// OPTIONAL: passes (credentials + permit) are delivered over WhatsApp, so an
  /// email is only a nice-to-have for the emailed copy / rejection notice — not
  /// required. Accepts a blank string (the form default) or omitted; the server
  /// treats any falsy value as "no email".
  email: z.string().trim().email().max(254).toLowerCase().or(z.literal("")).nullish(),
  phoneNumber: phoneSchema,
  /// Identity document. National ID or passport — the number is the key used
  /// to auto-merge a repeat delegate onto their existing account (adding the
  /// new unit) instead of creating a duplicate. Required so merging always
  /// has a key; `refineIdentity` enforces the NID-14 / passport rule server-side.
  /// Optional (defaults to NATIONAL_ID server-side) to keep the RHF form type
  /// consistent — a `.default()` would make it optional-in / required-out.
  idType: idTypeSchema.optional(),
  nationalId: idNumberSchema,
  /// Raw ID-card scan (stored for audit). OPTIONAL for a RENEWAL: when the
  /// national ID matches an existing account, approval reuses that account's
  /// stored ID + face, so the owner needn't re-upload. The server rejects a
  /// missing scan for a genuinely NEW delegate (see submissions.service).
  idCardKey: storageKeySchema.nullish(),
  /// Cropped face photo from the ID scan, forwarded to HikCentral for gate
  /// face recognition once the delegate is approved. OPTIONAL on renewal for
  /// the same reason as `idCardKey`.
  facePhotoKey: storageKeySchema.nullish()
}).superRefine(refineIdentity);
var createSubmissionRequestSchema = z.object({
  /// The unit being delegated — selected from the public unit picker, so
  /// it must reference an existing Unit row.
  unitId: z.string().uuid({ message: "Please choose your unit from the list" }),
  ownerFirstName: personNameSchema,
  ownerLastName: personNameSchema,
  /// Owner contact — required so the admin can reach the owner and so we can
  /// email them a summary of their submission (and the review outcome).
  ownerPhoneNumber: phoneSchema,
  ownerEmail: z.string().trim().email().max(254).toLowerCase(),
  delegates: z.array(submissionDelegateSchema).min(1).max(MAX_DELEGATES_PER_SUBMISSION),
  /// The single delegation window shared by every delegate. `duration` picks
  /// the length; `startDate` is the owner-chosen first day (today or later);
  /// the end date is derived (start + duration) and honoured at approval.
  duration: submissionDurationSchema,
  startDate: isoDateSchema.refine(
    (s) => s >= (/* @__PURE__ */ new Date()).toISOString().slice(0, 10),
    "Start date cannot be in the past"
  ),
  /// Unit-level services the owner ticks (see submissionServicesSchema).
  services: submissionServicesSchema,
  /// Optional free-text note from the owner to the reviewing admin.
  notes: z.string().trim().max(2e3).optional(),
  /// Supporting files (payment receipt, contracts, extra docs) — all
  /// OPTIONAL. Submitting none is allowed; any row that IS present must be a
  /// complete, labelled file (the form only shows rows the owner adds).
  attachments: z.array(submissionAttachmentSchema).max(MAX_SUBMISSION_ATTACHMENTS),
  /// The owner's hand-drawn signature, captured as a PNG and uploaded.
  signatureKey: storageKeySchema,
  /// Terms acceptance — must be explicitly true to submit.
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: "You must accept the terms and conditions" })
  }),
  /// Language the owner filled the form in. Persisted so every email this
  /// submission triggers (owner + each delegate) is sent in the same language.
  /// Optional on the wire (defaults to Arabic server-side); the public form
  /// always stamps it from the active UI language.
  locale: localeSchema.optional()
});
var renewLookupRequestSchema = z.object({
  unitId: z.string().uuid({ message: "Please choose your unit from the list" }),
  nationalId: idNumberSchema
});
var renewLookupResponseSchema = z.object({
  found: z.boolean(),
  renewable: z.boolean(),
  message: z.string().nullable(),
  delegate: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNumber: z.string(),
    idType: idTypeSchema,
    nationalId: z.string(),
    hasIdCard: z.boolean(),
    hasFace: z.boolean()
  }).nullable()
});
var updateSubmissionRequestSchema = createSubmissionRequestSchema.omit({ unitId: true });
var editableSubmissionResponseSchema = z.object({
  id: z.string().uuid(),
  status: submissionStatusSchema,
  unitId: z.string().uuid().nullable(),
  unitNumber: z.string(),
  payload: z.record(z.unknown())
});
var submissionResponseSchema = z.object({
  id: z.string().uuid(),
  type: submissionTypeSchema,
  status: submissionStatusSchema,
  unitNumber: z.string(),
  /// The selected unit (null for legacy rows submitted before the picker).
  unitId: z.string().uuid().nullable(),
  /// Split owner name (the form's "اسم مالك الوحدة"). Legacy rows expose
  /// only the combined `requesterName`.
  ownerFirstName: z.string().nullable(),
  ownerLastName: z.string().nullable(),
  /// Combined owner/requester name — kept for list views, search and
  /// backwards compatibility with legacy submissions.
  requesterName: z.string(),
  requesterPhone: z.string().nullable(),
  requesterEmail: z.string().nullable(),
  /// Document + consent artefacts shown to the reviewing admin.
  signatureKey: z.string().nullable(),
  acceptedTerms: z.boolean(),
  /// Language the owner used (drives every email for this submission).
  locale: z.string(),
  payload: z.record(z.unknown()),
  reviewedById: z.string().uuid().nullable(),
  /// Name of the admin who reviewed/approved (and possibly modified the
  /// duration). Shown in the dashboard — never in owner/delegate emails.
  reviewedByName: z.string().nullable(),
  reviewedAt: z.string().datetime({ offset: true }).nullable(),
  rejectionReason: z.string().nullable(),
  /// How many times this submission was reopened after a rejection. > 0 drives
  /// the "Reopened ×N" badge.
  reopenCount: z.number().int().nonnegative(),
  /// Last admin edit stamp (null if never edited by an admin). The editor's
  /// name + the field diff live in the change-history timeline.
  lastEditedAt: z.string().datetime({ offset: true }).nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true })
});
var createSubmissionResponseSchema = z.object({
  id: z.string().uuid(),
  status: submissionStatusSchema,
  createdAt: z.string().datetime({ offset: true })
});
var listSubmissionsQuerySchema = paginationQuerySchema.merge(sortQuerySchema(SUBMISSION_SORT_FIELDS)).extend({
  status: submissionStatusSchema.optional(),
  type: submissionTypeSchema.optional(),
  search: z.string().trim().min(1).max(80).optional(),
  /// Inclusive createdAt range (ISO datetime).
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional()
});
var rejectSubmissionRequestSchema = z.object({
  reason: z.string().trim().min(3).max(500)
});
var adminEditDelegateSchema = z.object({
  firstName: personNameSchema,
  lastName: personNameSchema,
  // Optional (passes go over WhatsApp); a blank string counts as "no email".
  email: z.string().trim().email().max(254).toLowerCase().or(z.literal("")).nullish(),
  phoneNumber: phoneSchema
});
var adminUpdateSubmissionRequestSchema = z.object({
  ownerFirstName: personNameSchema,
  ownerLastName: personNameSchema,
  ownerPhoneNumber: phoneSchema,
  ownerEmail: z.string().trim().email().max(254).toLowerCase(),
  duration: submissionDurationSchema,
  startDate: isoDateSchema,
  services: submissionServicesSchema,
  notes: z.string().trim().max(2e3).optional(),
  /// Must match the existing delegate count, in the same order (no add/remove).
  delegates: z.array(adminEditDelegateSchema).min(1).max(MAX_DELEGATES_PER_SUBMISSION),
  /// Why the edit was made — shown in the change history. Required.
  editNote: z.string().trim().min(3).max(500)
});
var updateApprovedServicesRequestSchema = z.object({
  services: submissionServicesSchema,
  /// Re-deliver each active delegate's pass showing the new services. A WhatsApp
  /// re-send issues a FRESH one-time password (Meta permits only our approved
  /// template, and the original password is never stored); an email re-send does
  /// the same, so the delegate always ends up with a working login. Leave off to
  /// update the stored passes silently (each delegate keeps their password) and
  /// re-send later from the delegate screen.
  resend: z.boolean().default(true),
  /// Which channel(s) to re-send on. Ignored when `resend` is false. Mirrors the
  /// delegate-screen resend channels (kept local so the submissions contract has
  /// no cross-file dependency).
  channel: z.enum(["both", "whatsapp", "email"]).default("both"),
  /// Why the change was made — recorded permanently in the change history.
  editNote: z.string().trim().min(3).max(500)
});
var servicesResendResultSchema = z.object({
  delegatedUserId: z.string(),
  fullName: z.string(),
  emailSent: z.boolean(),
  whatsappSent: z.boolean(),
  /// Set when the delegate was skipped (e.g. banned, or no active pass on this
  /// unit) or the re-send failed; null when the pass went out.
  skippedReason: z.string().nullable()
});
var updateApprovedServicesResponseSchema = z.object({
  submission: submissionResponseSchema,
  /// How many delegate permit snapshots were rewritten with the new services.
  snapshotsUpdated: z.number().int(),
  /// Per-delegate re-send outcomes (empty when `resend` was false).
  resent: z.array(servicesResendResultSchema)
});
var unitServicesResponseSchema = z.object({
  services: submissionServicesSchema,
  /// Active (APPROVED) delegates on the unit whose pass carries a snapshot — the
  /// ones a services edit would rewrite and re-issue.
  delegateCount: z.number().int()
});
var updateUnitServicesRequestSchema = z.object({
  services: submissionServicesSchema,
  /// Re-deliver each active delegate's pass showing the new services (a WhatsApp
  /// re-send issues a fresh one-time password). Off = update the passes silently.
  resend: z.boolean().default(true),
  channel: z.enum(["both", "whatsapp", "email"]).default("both"),
  /// Why the change was made — recorded in the admin action audit.
  editNote: z.string().trim().min(3).max(500)
});
var updateUnitServicesResponseSchema = z.object({
  /// How many delegate permit snapshots on the unit were rewritten.
  snapshotsUpdated: z.number().int(),
  /// Per-delegate re-send outcomes (empty when `resend` was false).
  resent: z.array(servicesResendResultSchema)
});
var reopenSubmissionRequestSchema = z.object({
  note: z.string().trim().min(3).max(500)
});
var submissionAuditActionSchema = z.enum([
  "CREATED",
  "EDITED",
  "REOPENED",
  "APPROVED",
  "REJECTED"
]);
var submissionChangeSchema = z.object({
  path: z.string(),
  label: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable()
});
var submissionAuditResponseSchema = z.object({
  id: z.string().uuid(),
  action: submissionAuditActionSchema,
  actorType: z.enum(["ADMIN", "OWNER"]),
  actorName: z.string(),
  actorEmail: z.string().nullable(),
  beforeStatus: submissionStatusSchema.nullable(),
  afterStatus: submissionStatusSchema.nullable(),
  changes: z.array(submissionChangeSchema),
  note: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true })
});
var approveSubmissionRequestSchema = z.object({
  note: z.string().trim().max(1e3).optional(),
  duration: submissionDurationSchema.optional(),
  startDate: isoDateSchema.optional(),
  /// Flag a violation against the delegate at approval time. When true, a
  /// `violationNote` is required and is printed on the delegate's permit PDF.
  violation: z.boolean().optional(),
  violationNote: z.string().trim().max(2e3).optional()
}).superRefine((v, ctx) => {
  if (v.violation && (!v.violationNote || v.violationNote.length < 3)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["violationNote"],
      message: "A violation note is required when flagging a violation"
    });
  }
});
var submissionDelegateMatchKinds = [
  "new",
  "renewal",
  "adds_unit",
  "reactivating",
  "banned"
];
var submissionDelegateMatchSchema = z.object({
  index: z.number().int(),
  nationalId: z.string().nullable(),
  fullName: z.string(),
  kind: z.enum(submissionDelegateMatchKinds),
  /// Their current access end (the unit link's for a renewal, else account-wide)
  /// as ISO — lets the UI warn when a custom start date would leave a gap.
  currentValidTo: z.string().nullable()
});
var submissionDelegateMatchesResponseSchema = z.array(submissionDelegateMatchSchema);
var WORKER_SORT_FIELDS = ["fullName", "status", "validTo", "createdAt"];
var createWorkerRequestBase = z.object({
  fullName: z.string().trim().min(2).max(160),
  phoneNumber: optionalPhoneSchema,
  idType: idTypeSchema.optional(),
  nationalId: idNumberSchema,
  unitId: z.string().uuid(),
  validFrom: z.string().datetime({ offset: true }).nullish(),
  validTo: z.string().datetime({ offset: true }).nullish(),
  notes: z.string().trim().max(500).nullish(),
  /// Storage keys returned by POST /files. The face photo is required by
  /// HikCentral worker enrollment; the raw ID-card scan is forwarded too.
  /// Optional at the contract level so the record can be created first and
  /// the photo attached later, but sync fails without a face photo.
  facePhotoKey: z.string().trim().min(1).max(512).nullish(),
  idCardKey: z.string().trim().min(1).max(512)
});
var createWorkerRequestSchema = createWorkerRequestBase.superRefine(refineIdentity);
var bulkImportWorkerRowSchema = createWorkerRequestBase.extend({ idCardKey: z.string().trim().min(1).max(512).optional() }).superRefine(refineIdentity);
var MAX_WORKERS_PER_UNIT_FOR_DELEGATE = 10;
var createWorkerByDelegateRequestSchema = z.object({
  unitId: z.string().uuid(),
  fullName: z.string().trim().min(2).max(160),
  phoneNumber: optionalPhoneSchema,
  idType: idTypeSchema.optional(),
  nationalId: idNumberSchema,
  validFrom: z.string().datetime({ offset: true }).nullish(),
  validTo: z.string().datetime({ offset: true }).nullish(),
  notes: z.string().trim().max(500).nullish(),
  facePhotoKey: z.string().trim().min(1).max(512).nullish(),
  idCardKey: z.string().trim().min(1).max(512)
}).superRefine(refineIdentity);
var updateWorkerByDelegateRequestSchema = z.object({
  fullName: z.string().trim().min(2).max(160).optional(),
  phoneNumber: optionalPhoneSchema,
  validFrom: z.string().datetime({ offset: true }).nullish(),
  validTo: z.string().datetime({ offset: true }).nullish(),
  notes: z.string().trim().max(500).nullish(),
  /// Replacing the face photo re-enrolls the worker in HikCentral (the sync
  /// worker only (re)enrolls faces via the create flow). Optional — omitted
  /// keys leave the existing photo untouched.
  facePhotoKey: z.string().trim().min(1).max(512).nullish()
});
var updateWorkerRequestSchema = z.object({
  fullName: z.string().trim().min(2).max(160).optional(),
  phoneNumber: optionalPhoneSchema,
  unitId: z.string().uuid().optional(),
  status: personStatusSchema.optional(),
  validFrom: z.string().datetime({ offset: true }).nullish(),
  validTo: z.string().datetime({ offset: true }).nullish(),
  notes: z.string().trim().max(500).nullish()
});
var workerResponseSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  phoneNumber: z.string().nullable(),
  idType: idTypeSchema,
  nationalId: z.string().nullable(),
  unitId: z.string().uuid(),
  unitNumber: z.string(),
  addedByDelegatedUserId: z.string().uuid().nullable(),
  status: personStatusSchema,
  validFrom: z.string().datetime({ offset: true }).nullable(),
  validTo: z.string().datetime({ offset: true }).nullable(),
  hikSyncStatus: hikSyncStatusSchema,
  hikSyncedAt: z.string().datetime({ offset: true }).nullable(),
  hikSyncError: z.string().nullable(),
  /// True when the HikCentral webhook reported a face match against a
  /// known blocked person. status will also be 'BLOCKED' in that case;
  /// the boolean exists so the admin UI can distinguish admin-blocked
  /// (manual) from upstream-blocked (security incident).
  blockedByExternalApi: z.boolean(),
  matchedBlockedPersonId: z.string().nullable(),
  /// Detailed Arabic narrative composed by the webhook handler. Shown
  /// in the admin detail panel and the audit log.
  externalBlockedReason: z.string().nullable(),
  facePhotoKey: z.string().nullable(),
  idCardKey: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true })
});
var listWorkersQuerySchema = paginationQuerySchema.merge(sortQuerySchema(WORKER_SORT_FIELDS)).extend({
  search: z.string().trim().min(1).max(80).optional(),
  status: personStatusSchema.optional(),
  unitId: z.string().uuid().optional(),
  /// Inclusive createdAt range (ISO datetime).
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional()
});
var BULK_WORKER_CSV_HEADERS = [
  "fullName",
  "phoneNumber",
  "nationalId",
  "unitNumber",
  "validFrom",
  "validTo",
  "notes"
];
var bulkImportWorkersRequestSchema = z.object({
  /// Raw CSV text. Max ~1 MB (≈ 10k rows). Frontend should read the
  /// uploaded file via FileReader and POST it as a JSON string.
  csv: z.string().min(1).max(1048576)
});
var bulkImportWorkerErrorSchema = z.object({
  /// 1-based row number in the source CSV (header is row 1, first data
  /// row is row 2). Lets admins jump straight to the bad cell in Excel.
  rowNumber: z.number().int().positive(),
  fullName: z.string().nullable(),
  nationalId: z.string().nullable(),
  code: z.enum([
    "VALIDATION_FAILED",
    "UNIT_NOT_FOUND",
    "NATIONAL_ID_CONFLICT",
    "WORKER_BANNED",
    "UNKNOWN_ERROR"
  ]),
  message: z.string()
});
var bulkImportWorkersResponseSchema = z.object({
  totalRows: z.number().int().nonnegative(),
  createdCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  createdIds: z.array(z.string().uuid()),
  errors: z.array(bulkImportWorkerErrorSchema)
});

// src/units/units.ts
var UNIT_SORT_FIELDS = ["unitNumber", "building", "floor", "createdAt"];
var unitNumberSchema = z.string().trim().min(1).max(40).regex(/^[A-Za-z0-9\-\/\s]+$/, "Unit number must be alphanumeric, dashes or slashes");
var createUnitRequestSchema = z.object({
  unitNumber: unitNumberSchema,
  building: z.string().trim().min(1).max(60).nullish(),
  floor: z.string().trim().min(1).max(20).nullish(),
  notes: z.string().trim().max(500).nullish(),
  /// The owner (proprietor/tenant) this unit belongs to. The link lives on
  /// the unit because an owner may hold several units. `null` detaches it.
  ownerId: z.string().uuid().nullish()
});
var updateUnitRequestSchema = createUnitRequestSchema.partial().extend({
  isBlocked: z.boolean().optional(),
  blockedReason: z.string().trim().max(500).nullish()
});
var unitOwnerSummarySchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  phoneNumber: z.string(),
  email: z.string().nullable(),
  nationalId: z.string().nullable(),
  isBlocked: z.boolean()
});
var unitResponseSchema = z.object({
  id: z.string().uuid(),
  unitNumber: z.string(),
  building: z.string().nullable(),
  floor: z.string().nullable(),
  notes: z.string().nullable(),
  isBlocked: z.boolean(),
  blockedAt: z.string().datetime({ offset: true }).nullable(),
  blockedReason: z.string().nullable(),
  ownerId: z.string().uuid().nullable(),
  owner: unitOwnerSummarySchema.nullable(),
  delegatedUserCount: z.number().int().nonnegative(),
  workerCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true })
});
var unitDetailResponseSchema = unitResponseSchema.extend({
  owner: ownerResponseSchema.nullable(),
  delegatedUsers: z.array(delegatedUserResponseSchema),
  workers: z.array(workerResponseSchema)
});
var unitOptionSchema = z.object({
  id: z.string().uuid(),
  unitNumber: z.string(),
  building: z.string().nullable(),
  floor: z.string().nullable()
});
var listUnitOptionsQuerySchema = z.object({
  search: z.string().trim().min(1).max(80).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20)
});
var booleanQueryParam = z.union([z.boolean(), z.enum(["true", "false"])]).transform((v) => typeof v === "boolean" ? v : v === "true").optional();
var listUnitsQuerySchema = paginationQuerySchema.merge(sortQuerySchema(UNIT_SORT_FIELDS)).extend({
  search: z.string().trim().min(1).max(80).optional(),
  isBlocked: z.union([z.boolean(), z.enum(["true", "false"])]).transform((v) => typeof v === "boolean" ? v : v === "true").optional(),
  /// Filter to units owned by this owner. `none` returns unassigned units.
  ownerId: z.union([z.string().uuid(), z.literal("none")]).optional(),
  /// Whether the unit has an owner assigned (true) or is unassigned (false).
  hasOwner: booleanQueryParam,
  /// Whether the unit has at least one delegated user attached.
  hasDelegates: booleanQueryParam,
  /// Whether the unit has at least one worker attached.
  hasWorkers: booleanQueryParam
});
var ADMIN_ACTIVITY_SORT_FIELDS = ["createdAt", "action"];
var adminActivityResponseSchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  adminId: z.string().uuid().nullable(),
  adminEmail: z.string().nullable(),
  adminFullName: z.string().nullable(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  requestId: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true })
});
var listAdminActivityQuerySchema = paginationQuerySchema.merge(sortQuerySchema(ADMIN_ACTIVITY_SORT_FIELDS)).extend({
  action: z.string().trim().min(1).optional(),
  adminId: z.string().uuid().optional(),
  targetType: z.string().trim().min(1).optional(),
  targetId: z.string().trim().min(1).optional(),
  /// ISO date — inclusive lower bound.
  from: z.string().datetime({ offset: true }).optional(),
  /// ISO date — inclusive upper bound.
  to: z.string().datetime({ offset: true }).optional()
});
var sessionResponseSchema = z.object({
  id: z.string().uuid(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  expiresAt: z.string().datetime({ offset: true })
});
var systemEventResponseSchema = z.object({
  id: z.string().uuid(),
  eventType: z.string(),
  aggregateType: z.string(),
  aggregateId: z.string(),
  attemptCount: z.number().int().min(0),
  lastError: z.string().nullable(),
  consumedAt: z.string().datetime({ offset: true }).nullable(),
  failedAt: z.string().datetime({ offset: true }).nullable(),
  nextAttemptAt: z.string().datetime({ offset: true }),
  createdAt: z.string().datetime({ offset: true })
});
var listSystemEventsQuerySchema = paginationQuerySchema.extend({
  /// 'pending' = not consumed, not failed.
  /// 'failed' = failedAt set (dead-lettered).
  /// 'consumed' = consumedAt set.
  /// 'all' = no filter.
  state: z.enum(["pending", "failed", "consumed", "all"]).default("all"),
  eventType: z.string().trim().min(1).max(80).optional(),
  aggregateType: z.string().trim().min(1).max(80).optional()
});
var messageLogChannels = ["whatsapp", "email"];
var messageLogStatuses = [
  "queued",
  "sent",
  "delivered",
  "read",
  "failed",
  "skipped"
];
var listMessageLogsQuerySchema = paginationQuerySchema.extend({
  channel: z.enum(messageLogChannels).optional(),
  status: z.enum(messageLogStatuses).optional(),
  /// Only WhatsApp messages the gateway accepted but never confirmed delivered —
  /// the "sent but never landed" bucket.
  unconfirmedOnly: z.coerce.boolean().optional(),
  /// Free text over recipient (phone/email) + kind.
  search: z.string().trim().min(1).max(200).optional(),
  from: z.string().optional(),
  to: z.string().optional()
});
var messageLogResponseSchema = z.object({
  id: z.string().uuid(),
  channel: z.enum(messageLogChannels),
  recipient: z.string(),
  kind: z.string(),
  subject: z.string().nullable(),
  status: z.enum(messageLogStatuses),
  providerStatus: z.string().nullable(),
  providerMessageId: z.string().nullable(),
  objectId: z.string().nullable(),
  relatedType: z.string().nullable(),
  relatedId: z.string().nullable(),
  error: z.string().nullable(),
  attempts: z.number().int(),
  sentAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  lastCheckedAt: z.string().nullable(),
  createdAt: z.string()
});
var messageLogStatsResponseSchema = z.object({
  total: z.number().int(),
  whatsapp: z.number().int(),
  email: z.number().int(),
  delivered: z.number().int(),
  failed: z.number().int(),
  unconfirmed: z.number().int()
});

export { ADMIN_ACTIVITY_SORT_FIELDS, ADMIN_SORT_FIELDS, BULK_IMPORT_ERROR_CODES, BULK_WORKER_CSV_HEADERS, DELEGATED_USER_SORT_FIELDS, ENTRY_LOG_SORT_FIELDS, MAX_DELEGATES_PER_SUBMISSION, MAX_SUBMISSION_ATTACHMENTS, MAX_WORKERS_PER_UNIT_FOR_DELEGATE, OWNER_CSV_HEADERS, OWNER_SORT_FIELDS, PERMISSIONS, REMINDER_SORT_FIELDS, ROLE_PERMISSIONS, SUBMISSION_SORT_FIELDS, UNIT_CSV_HEADERS, UNIT_SORT_FIELDS, WORKER_SORT_FIELDS, addDelegatedUserUnitRequestSchema, addSubmissionDuration, adminActivityResponseSchema, adminEditDelegateSchema, adminLoginRequestSchema, adminLoginResponseSchema, adminResponseSchema, adminRoleSchema, adminStatsResponseSchema, adminUpdateSubmissionRequestSchema, approveSubmissionRequestSchema, blockDelegatedUserUnitRequestSchema, bootstrapAdminRequestSchema, brandBankAccountSchema, brandFormSchema, brandingResponseSchema, bulkCancelDelegationsRequestSchema, bulkCancelDelegationsResponseSchema, bulkImportPreviewRowSchema, bulkImportRequestSchema, bulkImportResponseSchema, bulkImportRowErrorSchema, bulkImportWorkerErrorSchema, bulkImportWorkerRowSchema, bulkImportWorkersRequestSchema, bulkImportWorkersResponseSchema, bulkRetryFailedSyncResponseSchema, bulkUpdateUnitWindowsRequestSchema, bulkUpdateUnitWindowsResponseSchema, cancelDelegationRequestSchema, changePasswordRequestSchema, createAdminRequestSchema, createDelegatedUserRequestSchema, createFormRuleRequestSchema, createOwnerRequestSchema, createReminderRequestSchema, createSubmissionRequestSchema, createSubmissionResponseSchema, createUnitRequestSchema, createWorkerByDelegateRequestSchema, createWorkerRequestSchema, delegatePrincipalSchema, delegatedUserResponseSchema, delegatedUserUnitSchema, durationToMonths, editableSubmissionResponseSchema, egyptianMobileSchema, egyptianNationalIdSchema, entryDirectionSchema, entryLogStatsResponseSchema, errorResponseSchema, estimateElectricityFee, formRuleResponseSchema, formServiceSchema, formSettingsResponseSchema, gateSyncRetryResultSchema, gateSyncRetryStartedResponseSchema, gateSyncStatusResponseSchema, hasAllPermissions, hasAnyPermission, hasPermission, hikSyncStatusSchema, idNumberSchema, idTypeSchema, isoTimestampSchema, listAdminActivityQuerySchema, listAdminsQuerySchema, listDelegatedUsersQuerySchema, listEntryLogsQuerySchema, listMessageLogsQuerySchema, listOwnersQuerySchema, listRemindersQuerySchema, listSubmissionsQuerySchema, listSystemEventsQuerySchema, listUnitOptionsQuerySchema, listUnitsQuerySchema, listWorkersQuerySchema, localeSchema, localizedListSchema, localizedTextSchema, meResponseSchema, messageLogChannels, messageLogResponseSchema, messageLogStatsResponseSchema, messageLogStatuses, needsUtilitiesBundleReceipt, optionalPhoneSchema, ownerResponseSchema, paginatedResponseSchema, paginationMetaSchema, paginationQuerySchema, passwordResetConfirmSchema, passwordResetRequestSchema, personStatusSchema, phoneSchema, publicFormSettingsResponseSchema, recordAccessEventRequestSchema, recordAccessEventResponseSchema, refineIdentity, refreshResponseSchema, rejectSubmissionRequestSchema, reminderResponseSchema, reminderTypeSchema, renewLookupRequestSchema, renewLookupResponseSchema, reopenSubmissionRequestSchema, resendChannelSchema, resendCredentialsRequestSchema, resendCredentialsResponseSchema, resetAdminPasswordRequestSchema, resetAdminPasswordResponseSchema, servicesResendResultSchema, sessionResponseSchema, sortDirectionSchema, sortQuerySchema, submissionAttachmentSchema, submissionAuditActionSchema, submissionAuditResponseSchema, submissionChangeSchema, submissionDelegateMatchKinds, submissionDelegateMatchSchema, submissionDelegateMatchesResponseSchema, submissionDelegateSchema, submissionDurationSchema, submissionPersonSchema, submissionResponseSchema, submissionServicesSchema, submissionStatusSchema, submissionTypeSchema, systemEventResponseSchema, timestampsSchema, unitDetailResponseSchema, unitNumberSchema, unitOptionSchema, unitOwnerSummarySchema, unitResponseSchema, unitServicesResponseSchema, updateAdminRequestSchema, updateApprovedServicesRequestSchema, updateApprovedServicesResponseSchema, updateDelegatedUserRequestSchema, updateFormRuleRequestSchema, updateFormSettingsRequestSchema, updateOwnerRequestSchema, updateSubmissionRequestSchema, updateUnitRequestSchema, updateUnitServicesRequestSchema, updateUnitServicesResponseSchema, updateWorkerByDelegateRequestSchema, updateWorkerRequestSchema, userLoginRequestSchema, userLoginResponseSchema, usernameSchema, uuidSchema, validationErrorResponseSchema, workerEntryLogResponseSchema, workerResponseSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map